<template>
  <div>
    <!-- Compact View -->
    <template v-if="compact">
      <modal-dialog
        :visible="visible"
        persistent
        max-width="290"
        :title="$t('title.filter')"
      >
        <v-row class="mt-2">
          <v-col
            v-if="!filterStatusUpcoming"
            cols="6"
          >
            <time-picker-field
              :value="filterTimeFrom"
              hide-details
              :label="$t('label.filter_time_from')"
              class="pa-0 ma-0"
              :append-outer-icon="filterTimeFromDirty ? 'mdi-close' : null"
              @click-append-outer="changeFilterTimeFrom(null)"
              @input="changeFilterTimeFrom"
            />
          </v-col>
          <v-col
            v-if="!filterStatusUpcoming"
            cols="6"
          >
            <time-picker-field
              :value="filterTimeTo"
              hide-details
              :label="$t('label.filter_time_until')"
              class="pa-0 ma-0"
              :append-outer-icon="filterTimeToDirty ? 'mdi-close' : null"
              @click-append-outer="changeFilterTimeTo(null)"
              @input="changeFilterTimeTo"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              :value="filterSection"
              hide-details
              :items="filterSectionItems"
              :append-outer-icon="filterSectionDirty ? 'mdi-close' : null"
              :label="$t('label.section')"
              @click-append-outer="changeFilterSection(null)"
              @input="changeFilterSection"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              :value="filterStatus"
              hide-details
              :items="filterStatusItems"
              :append-outer-icon="filterStatusDirty ? 'mdi-close' : null"
              :label="$t('label.status')"
              @click-append-outer="changeFilterStatus(null)"
              @input="changeFilterStatus"
            />
          </v-col>
        </v-row>

        <template #actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="close"
          >
            {{ $t('button.ok') }}
          </v-btn>
          <v-btn
            color="error"
            text
            @click="resetFilter"
          >
            {{ $t('button.clear_filter') }}
          </v-btn>
        </template>
      </modal-dialog>
    </template>
    <!-- Full View -->
    <template v-else>
      <v-row
        class="d-flex pt-0 ma-0 pl-2 pr-2"
        style="height: 64px"
      >
        <v-col
          v-if="!filterStatusUpcoming"
          class="d-flex pa-0 pl-2 pr-1 ma-0 align-center"
        >
          <div style="width: 80px">
            <time-picker-field
              v-model="filterTimeFrom"
              dense
              hide-details
              :label="$t('label.filter_time_from')"
              class="pa-0 ma-0"
              :append-outer-icon="filterTimeFromDirty ? 'mdi-close' : null"
              @click-append-outer="changeFilterTimeFrom(null)"
              @input="changeFilterTimeFrom"
            />
          </div>
          <div class="pa-5">
            -
          </div>
          <div style="width: 80px">
            <time-picker-field
              v-model="filterTimeTo"
              dense
              hide-details
              :label="$t('label.filter_time_until')"
              class="pa-0 ma-0"
              :append-outer-icon="filterTimeToDirty ? 'mdi-close' : null"
              @click-append-outer="changeFilterTimeTo(null)"
              @input="changeFilterTimeTo"
            />
          </div>
        </v-col>
        <v-col class="flex-shrink-1 pa-0 ma-0" />
        <v-col
          class="pa-0 ma-0 d-flex align-center"
          style="max-width: 270px"
        >
          <v-select
            v-model="filterSection"
            class="pl=5 pr-5"
            dense
            height="26"
            hide-details
            :items="filterSectionItems"
            :label="$t('label.section')"
            :append-outer-icon="filterSectionDirty ? 'mdi-close' : null"
            @click:append-outer="changeFilterSection(null)"
            @input="changeFilterSection"
          />
        </v-col>
        <div class="pr-2 pa-0 ma-0 d-flex align-center">
          <v-select
            class="filter-status-select"
            :value="filterStatus"
            style="width: 180px"
            dense
            hide-details
            :items="filterStatusItems"
            :label="$t('label.status')"
            :append-outer-icon="filterStatusDirty ? 'mdi-close' : null"
            @click:append-outer="changeFilterStatus(null)"
            @input="changeFilterStatus"
          >
            <template #item="{item}">
              <span class="d-block w-100">{{ item.text }}</span>
              <div
                v-if="colorForStatus(item.value)"
                class="text-right"
              >
                <v-icon
                  x-small
                  :color="colorForStatus(item.value)"
                >
                  mdi-circle
                </v-icon>
              </div>
            </template>
            <template #append>
              <v-icon
                v-if="colorForStatus(filterStatus)"
                x-small
                style="padding-top: 7px;"
                :color="colorForStatus(filterStatus)"
              >
                mdi-circle
              </v-icon>
            </template>
          </v-select>
        </div>
      </v-row>
    </template>
  </div>
</template>

<script lang='ts'>

import Vue, { PropType } from 'vue';
import { ReservationStatus } from '@/model/Reservation';
import { reservationStatusAllItems, reservationStatusItems } from '@/util/status';
import ClosableDialog from '@/mixins/dialog-closable';
import Tab from '@/model/Tab';
import { colorForStatus } from '@/components/helpers/colors';

export default Vue.extend({
  name: 'GuestFilter',
  components: { },
  mixins: [ClosableDialog],
  props: {
    compact: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      filterStatusItems: reservationStatusAllItems(),
      statusItems: reservationStatusItems(),
    };
  },
  computed: {
    filterTimeTo(): string { return this.$tstore.state.filter.to; },
    filterTimeFrom(): string { return this.$tstore.state.filter.from; },
    filterStatus(): ReservationStatus { return this.$tstore.state.filter.status; },
    filterSection(): Tab | null { return this.$tstore.state.filter.tab; },
    filterSectionItems() {
      return [
        { text: this.$i18n.t('label.all_sections'), value: null },
        { divider: true },
        ...this.$tstore.state.settings.tabs,
      ];
    },
    filterTimeFromDirty(): boolean { return this.$tstore.getters.isFilterFrom; },
    filterTimeToDirty(): boolean { return this.$tstore.getters.isFilterTo; },
    filterStatusDirty(): boolean { return this.$tstore.getters.isFilterStatus; },
    filterSectionDirty(): boolean { return this.$tstore.getters.isFilterTab; },
    filterStatusUpcoming(): boolean { return this.$tstore.state.filter.status === ReservationStatus.Upcoming; },
  },
  methods: {
    resetFilter() {
      this.$tstore.dispatch('resetFilter');
      this.$emit('close', false);
    },
    applyFilter(p: { from?: string | null, to?: string | null, status?: ReservationStatus | null, tab?: Tab | null }) {
      this.$tstore.dispatch('applyFilter', p);
    },
    changeFilterTimeFrom(value: string | null) { this.applyFilter({ from: value }); },
    changeFilterTimeTo(value: string | null) { this.applyFilter({ to: value }); },
    changeFilterSection(value: number | null) {
      const tab = this.$tstore.state.settings.tabs.find((t) => t.id === value) ?? null;
      this.applyFilter({ tab });
    },
    changeFilterStatus(value: ReservationStatus | null) { this.applyFilter({ status: value }); },
    close() {
      this.$emit('close', false);
    },
    colorForStatus(status: ReservationStatus) {
      if (!this.isStatus(status)) return undefined;
      return colorForStatus(status);
    },
    isStatus(status: ReservationStatus) {
      return this.statusItems.find((si) => si.value === status);
    },
  },
});

</script>

<style scoped>

.filter-status-select ::v-deep .v-input__icon--append-outer {
  margin-left: 8px;
}

</style>
